import { DeliveredProcedureOutlined, SaveOutlined, ScissorOutlined } from '@ant-design/icons';
import { Tag, Slider, InputNumberProps, Space } from 'antd';
import gql from 'graphql-tag';
import { StoryFramesModel } from 'interface/models';
import { useState, forwardRef, useImperativeHandle } from 'react';
import { useAuthedMutation } from 'utils/qlAuth';
import _ from 'lodash';

interface LazyVideoPlayerProps {
  frame: StoryFramesModel;
  videoSrc: string;
  index: number;
  displayType: 'small' | 'medium' | 'big' | string;
  videoRef?: (el: HTMLVideoElement | null) => void;
}

export const LazyVideoPlayer = (props: LazyVideoPlayerProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hideTimer, setHideTimer] = useState<any>();
  const [trimRange, setTrimRange] = useState<[number, number]>([props.frame.videoCatStart || 0, props.frame.videoCatStop || 5]);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoSpeed, setVideoSpeed] = useState(props.frame.videoSpeed || 1);

  const [updateFrame] = useAuthedMutation(
    gql`
      mutation StoryController_updateFrame($frameId: Int!, $input: JSON!) {
        StoryController_updateFrame(frameId: $frameId, config: $input) {
          id
        }
      }
    `,
  );

  const resetTimer = () => {
    if (hideTimer) {
      clearTimeout(hideTimer);
      setHideTimer(30000);
    }
    setHideTimer(setTimeout(() => setIsLoaded(false), 30000));
  };
  const saveChanges = async () => {
    const newConfig = {
      videoCatStart: trimRange[0],
      videoCatStop: trimRange[1],
      videoSpeed: videoSpeed,
    };
    await updateFrame({
      variables: {
        frameId: props.frame.id,
        input: newConfig,
      },
    });
  };

  let cover = (
    <img
      src={props.frame.images[props.index]}
      alt={`Frame ${props.index}`}
      style={{
        height: '100%',
        maxWidth: '100%',
      }}
    />
  );

  return (
    <div
      style={{
        width: '420px',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        style={{
          height: props.displayType === 'small' ? '120px' : '240px',
          maxWidth: '800px',
          cursor: 'pointer',
          backgroundColor: '#ccc',
          display: 'flex',
          padding: '5px',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => {
          setIsLoaded(true);
          // Авто скрытие
          setHideTimer(setTimeout(() => setIsLoaded(false), 30000));
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsLoaded(true);
            // Авто скрытие
            setHideTimer(setTimeout(() => setIsLoaded(false), 30000));
          }
        }}
      >
        <br />
        <br />
        {isLoaded ? (
          <>
            <video
              controls
              style={{
                width: '800px',
                height: '100%',
              }}
              autoPlay
              onRateChange={e => {
                // @ts-ignore
                setVideoSpeed(e.target.playbackRate);
              }}
              onTimeUpdate={e => {
                // @ts-ignore
                const time = e.target?.currentTime;
                setCurrentTime(time);
                resetTimer();
              }}
              ref={el => {
                if (el && el.playbackRate !== videoSpeed) {
                  el.playbackRate = videoSpeed;
                }
                if (props.videoRef) props.videoRef(el);
              }}
            >
              <source src={props.videoSrc} type="video/mp4" />
              <track kind="captions" srcLang="en" label="English captions" src={`${props.videoSrc}.vtt`} />
              Your browser does not support the video tag.
            </video>
          </>
        ) : (
          cover
        )}
      </div>
      {(props.displayType === 'big' || isLoaded) && (
        <>
          <Slider
            range
            min={0}
            max={5}
            step={0.01}
            value={trimRange}
            onChange={v => {
              setTrimRange([v[0], v[1]]);
              resetTimer();
            }}
            tooltip={{ formatter: v => `${v} сек` }}
          />
          <div style={{ width: '800px' }}>
            <Space direction="horizontal">
              <span>
                Обрезка: {trimRange[0].toFixed(1)} – {trimRange[1].toFixed(1)}
              </span>
              <Tag
                icon={<ScissorOutlined />}
                style={{ cursor: 'pointer' }}
                onClick={e => {
                  setTrimRange([trimRange[0], currentTime]);
                }}
              >
                {(currentTime || 0).toFixed(1)}
              </Tag>
              <Space direction="horizontal">
                <span>Скорость: {(videoSpeed || 0).toFixed(1)}</span>
                <Slider
                  style={{
                    width: '200px',
                  }}
                  min={0.5}
                  max={2}
                  step={0.05}
                  value={videoSpeed}
                  onChange={v => setVideoSpeed(v)}
                  tooltip={{ formatter: v => `${v}x` }}
                />
              </Space>
              <Tag
                icon={<DeliveredProcedureOutlined />}
                style={{ cursor: 'pointer' }}
                onClick={e => {
                  const localPath = props.videoSrc.replace(/^.*?\/storage\//, '/ai-threshold/storage/');
                  navigator.clipboard.writeText(localPath);
                  e.stopPropagation();
                }}
              >
                Copy name
              </Tag>
              <Tag
                icon={<SaveOutlined />}
                style={{ cursor: 'pointer' }}
                onClick={e => {
                  saveChanges();
                }}
              >
                Save
              </Tag>
            </Space>
          </div>
        </>
      )}
    </div>
  );
};
